import * as React from 'react';

import { useIntl } from 'react-intl';
import { RadialBarChart, RadialBar, ResponsiveContainer, PolarAngleAxis } from 'recharts';

import { HomeStatistics, RADIAL_BAR_FILLS, StatisticZone } from 'src/features/home/types';
import { homeMessages } from 'src/features/home/utils/messages';
import { GeneralBlock } from 'src/registry/components/blocks/GeneralBlock';
import { SectionBlockSubtitle, SectionBlockTitle } from 'src/registry/components/blocks/SectionBlock';
import StatisticValue from 'src/registry/components/statistic/StatisticValue';
import { Skeleton } from 'src/registry/ui/Skeleton';
import { cn } from 'src/utils/shadcn';
import ComparisonBadge from 'src/features/home/components/ComparisonBadge';
import { Translation } from 'src/types';
import GeneralMessageBlock from 'src/registry/components/blocks/GeneralMessageBlock';

interface DashboardWidgetZonesProps {
    statsByZones?: StatisticZone[];
    totalStatsByZones?: HomeStatistics['total_stats_for_zones'];
    isLoading?: boolean;
    isError?: boolean;
}
export default function WidgetZones({
    statsByZones,
    totalStatsByZones,
    isLoading,
    isError,
}: DashboardWidgetZonesProps) {
    const { formatMessage, locale } = useIntl();
    const [selectedZone, setSelectedZone] = React.useState<StatisticZone>();

    const translate = React.useCallback(
        (translations?: Translation[]) => {
            if (!translations) return '-';
            const localeTranslation = translations.find((translation) => translation.language === locale)?.name;
            return localeTranslation ?? '-';
        },
        [locale],
    );

    return (
        <GeneralBlock className="flex-1">
            <div className="flex flex-col">
                <SectionBlockTitle>{formatMessage(homeMessages.freeSpacesByZones)}</SectionBlockTitle>
                <SectionBlockSubtitle>{formatMessage(homeMessages.freeSpacesByZonesDescription)}</SectionBlockSubtitle>
            </div>
            {isError ? (
                <GeneralMessageBlock variant="error" />
            ) : (
                <>
                    <div className="relative flex h-full w-full items-center justify-center">
                        {isLoading && <Skeleton className="aspect-square size-70 rounded-full" />}
                        {!isLoading && <div className="aspect-square w-80"></div>}
                        {/* Unfortunately radial bars cannot have different domain from each other, so we need to render
                        a new container for each entry. Additionally, we have to make some hacky calculations and effects as to get
                        the desired effect, since there are no props/implementations for it in the library */}
                        {!isLoading &&
                            totalStatsByZones &&
                            statsByZones?.map((zone, i) => {
                                const width =
                                    50 + (statsByZones?.length < 4 ? (statsByZones?.length < 2 ? 2 : 1) : 0) * (60 / 4);
                                const range = (110 - width) / statsByZones?.length;
                                const start = width + range * i - 0.5;
                                const end = width + range * (i + 1) + 2.5;
                                return (
                                    <ResponsiveContainer
                                        key={zone.zone}
                                        className="absolute aspect-square"
                                        width="100%"
                                        height="100%"
                                    >
                                        <RadialBarChart
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={`${start}%`}
                                            outerRadius={`${end}%`}
                                            margin={{ top: 15, right: 17.5, bottom: 15, left: 17.5 }}
                                            data={[
                                                {
                                                    total: zone.total_spaces,
                                                    occupied: zone.taken_spaces,
                                                    fill: RADIAL_BAR_FILLS[i % RADIAL_BAR_FILLS.length],
                                                },
                                            ]}
                                            startAngle={89}
                                            endAngle={-269}
                                            className={cn(
                                                'pointer-events-none relative transition-opacity',
                                                selectedZone?.zone === zone.zone ? 'opacity-50' : '',
                                            )}
                                            style={{ zIndex: 6 - i }}
                                        >
                                            <RadialBar
                                                background={{ fill: '#f3f4f6', stroke: '#fff', strokeWidth: '1%' }}
                                                cornerRadius="5%"
                                                dataKey="occupied"
                                                stroke="#fff"
                                                strokeWidth="1%"
                                                className="pointer-events-auto"
                                                onMouseEnter={() => setSelectedZone(zone)}
                                                onMouseLeave={() => setSelectedZone(undefined)}
                                            />
                                            <PolarAngleAxis
                                                type="number"
                                                style={{ opacity: '0%', visibility: 'hidden' }}
                                                domain={[0, zone.total_spaces]}
                                            />
                                        </RadialBarChart>
                                    </ResponsiveContainer>
                                );
                            })}
                        {!isLoading && totalStatsByZones && (
                            <StatisticValue
                                title={
                                    selectedZone?.translations
                                        ? translate(selectedZone.translations)
                                        : formatMessage(homeMessages.totalSpaces)
                                }
                                value={selectedZone?.free_spaces ?? totalStatsByZones?.currently_free_spaces}
                                totalValue={selectedZone?.total_spaces ?? totalStatsByZones?.total_spaces}
                                isLoading={isLoading}
                                className="absolute inset-0 m-auto justify-center"
                            />
                        )}
                    </div>
                    <ComparisonBadge
                        value={selectedZone?.compared_to_last_day ?? totalStatsByZones?.compared_to_last_day}
                    />
                </>
            )}
        </GeneralBlock>
    );
}
